import { Divider, Group, Stack } from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { showNotification } from '@mantine/notifications';
import type { DataAccessRequestCreateIn, ResourcesIn } from '@repo/api-codegen';
import {
	apiQueryKey,
	listAccessRequestsQueryKey,
	useCreateAccessRequest,
	useUpdateAccessRequest,
} from '@repo/api-codegen';
import { trackEvent } from '@repo/common/analytics';
import { integrationList } from '@repo/common/constants/integration/integrations';
import { UserRole } from '@repo/common/enums/UserRole';
import { Banner, Button, Select, Text, TextArea } from '@repo/foundations';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import { useCallback, useMemo, useRef, useState } from 'react';
import * as Yup from 'yup';
import { queryClient, useIntegrationList } from '../../../api';
import { useCheckIAMPermission } from '../../../utils/authorization/roles';
import { useFeatureFlags } from '../../../utils/featureFlags/useFeatureFlags';
import IntegrationLogo from '../../IntegrationLogo';
import { ResourceSelector } from './ResourceSelector';

export interface DataAccessRequestCreateModalProps {
	onClose: () => void;
	request?: Partial<DataAccessRequestCreateIn> & { id?: string };
	withIntegrationSelector?: boolean;
	withResourcesSelector?: boolean;
}

export type ResourceLevel = ResourcesIn['resource_level'];

export function DataAccessRequestCreateModal({
	onClose,
	request,
	withIntegrationSelector = true,
	withResourcesSelector = true,
}: DataAccessRequestCreateModalProps) {
	const { data: integrationsResult } = useIntegrationList({});
	const [hasNoResources, setHasNoResources] = useState(false);
	const { servicenowAccessRequestForm } = useFeatureFlags();
	const [manualResourceLevel, setManualResourceLevel] =
		useState<ResourceLevel | null>(null);

	const resourceLevel = useMemo<ResourceLevel | null>(
		() => (servicenowAccessRequestForm ? 'table' : manualResourceLevel),
		[servicenowAccessRequestForm, manualResourceLevel]
	);

	const { hasPermission: hasIntegrationsAccess } = useCheckIAMPermission({
		v1AllowedRoles: [UserRole.ADMIN],
		v2Permission: 'Integrations.Manage',
	});

	const { mutate: createAccessRequest, isPending: isLoading } =
		useCreateAccessRequest({
			onSuccess: () => {
				queryClient.invalidateQueries({
					queryKey: apiQueryKey('integration_v2/data-access-requests'),
				});
				showNotification({
					title: 'Access request created',
					message: 'Your request has been created and is pending approval',
					color: 'green',
				});
			},
		});

	const { mutate: updateAccessRequest } = useUpdateAccessRequest({
		onSuccess: () => {
			showNotification({
				title: 'Access request updated',
				message: 'Your request has been updated',
				color: 'green',
			});
		},
	});

	const integrationOptions = useMemo(
		() =>
			(integrationsResult?.results ?? [])
				.filter(
					(integration) =>
						integrationList.find(
							(localIntegration) =>
								localIntegration.type === integration.type &&
								localIntegration.supportsDataAccessRequests
						) && !!integration.access_request_enabled
				)
				.map((integration) => ({
					accessRequestPreference: integration.access_request_preference,
					label: integration.name,
					value: integration.id,
					icon: (
						<IntegrationLogo integrationType={integration.type} size={16} />
					),
				})),
		[integrationsResult]
	);

	const formik = useFormik({
		initialValues: {
			integrationId: request?.integration_id,
			resources: request?.requested_resources,
			duration: request?.requested_expires_at
				? dayjs(request.requested_expires_at).toDate()
				: undefined,
			reason: request?.requested_text ?? '',
			dataNeedExplanation: request?.data_need_explanation ?? '',
			dataSharedExternallyDescription:
				request?.data_shared_externally_description ?? '',
			dataUsageDescription: request?.data_usage_description ?? '',
			additionalInformation: request?.additional_information ?? '',
			requestingDepartment: request?.requesting_department ?? '',
		},
		validationSchema: Yup.object().shape({
			integrationId: Yup.string(),
			resources: Yup.array(),
			duration: Yup.date().nullable(),
			...(servicenowAccessRequestForm && {
				requestingDepartment: Yup.string().required(
					'Requesting department is required'
				),
				dataNeedExplanation: Yup.string().required(
					'Please explain your data needs'
				),
				dataSharedExternallyDescription: Yup.string().required(
					'Please describe if data will be shared externally'
				),
				dataUsageDescription: Yup.string().required(
					'Please describe how this data will be used'
				),
				additionalInformation: Yup.string().required(
					'Please provide any additional information'
				),
			}),
		}),
		validateOnBlur: false,
		validateOnChange: true,
		onSubmit: async (values) => {
			if (!values.resources || values.resources.length === 0) {
				formik.setFieldError('resources', 'At least one resource is required');
				return;
			}

			const baseBody = {
				integration_id: values.integrationId!,
				requested_resources: values.resources,
				requested_expires_at: values.duration
					? dayjs(values.duration).toISOString()
					: null,
				requested_text: values.reason,
			};

			const serviceNowFields = servicenowAccessRequestForm
				? {
						data_need_explanation: values.dataNeedExplanation || null,
						data_shared_externally_description:
							values.dataSharedExternallyDescription || null,
						data_usage_description: values.dataUsageDescription || null,
						additional_information: values.additionalInformation || null,
						requesting_department: values.requestingDepartment || null,
					}
				: {};

			if (request?.id) {
				await updateAccessRequest({
					pathParams: {
						requestId: request.id,
					},
					body: {
						...baseBody,
						...serviceNowFields,
					},
				});
			} else {
				await createAccessRequest({
					body: {
						...baseBody,
						...serviceNowFields,
					},
				});

				trackEvent('access_request/created', {
					has_duration: String(!!values.duration),
					resources_count: String(values.resources?.length ?? 0),
					integration_id: values.integrationId ?? '',
					with_reason: String(!!values.reason),
				});
			}

			queryClient.invalidateQueries({
				queryKey: listAccessRequestsQueryKey({}),
			});
			onClose();
		},
	});

	const handleOnResourcesLoaded = useCallback((count: number) => {
		setHasNoResources(count === 0);
	}, []);

	const shouldAutoSelectIntegration = useMemo(
		() =>
			!formik.values.integrationId &&
			integrationOptions?.length > 0 &&
			(servicenowAccessRequestForm || integrationOptions.length === 1),
		[
			formik.values.integrationId,
			integrationOptions,
			servicenowAccessRequestForm,
		]
	);

	const autoSelectedIntegrationId = useMemo(
		() => (shouldAutoSelectIntegration ? integrationOptions?.[0]?.value : null),
		[shouldAutoSelectIntegration, integrationOptions]
	);

	// Track if we've already auto-selected to avoid infinite loops
	const hasAutoSelectedRef = useRef(false);

	// Auto-select integration synchronously during render if needed
	if (
		autoSelectedIntegrationId &&
		!formik.values.integrationId &&
		!hasAutoSelectedRef.current
	) {
		hasAutoSelectedRef.current = true;
		formik.setFieldValue('integrationId', autoSelectedIntegrationId, false);
	}

	// Reset the ref if integration ID is cleared or changed manually
	if (
		!autoSelectedIntegrationId ||
		(formik.values.integrationId &&
			formik.values.integrationId !== autoSelectedIntegrationId)
	) {
		hasAutoSelectedRef.current = false;
	}

	const handleClose = useCallback(() => {
		formik.resetForm();
		onClose();
	}, [formik, onClose]);

	const handleResourceLevelChange = useCallback(
		(value: ResourceLevel | null) => {
			setManualResourceLevel(value);
		},
		[]
	);

	return (
		<form onSubmit={formik.handleSubmit}>
			<Stack spacing="md">
				{withIntegrationSelector && !servicenowAccessRequestForm && (
					<>
						{integrationOptions.length === 0 && (
							<Banner
								tone="warning"
								message="Access requests are not enabled for any integrations. Contact admin for access."
								inCard
								aria-label="Warning: Access requests are not enabled for any integrations. Contact admin for access."
							/>
						)}
						<Select
							data={integrationOptions}
							label="Integration"
							name="integrationId"
							placeholder="Select"
							value={formik.values.integrationId}
							error={formik.errors.integrationId}
							onChange={(value) => {
								formik.setFieldValue('integrationId', value);
								formik.setFieldValue('resources', [], true);
							}}
							onBlur={formik.handleBlur}
						/>
						{hasNoResources && (
							<Banner
								tone="info"
								message={
									hasIntegrationsAccess
										? 'None of your teams have access to this integration. You can make it available to your team in the integration settings.'
										: 'None of your teams have access to this integration. Ask your admin to make it available to your team before requesting access.'
								}
								inCard
								aria-label="Information: None of your teams have access to this integration."
							/>
						)}
					</>
				)}
				{withResourcesSelector && !servicenowAccessRequestForm && (
					<Select
						data={['database', 'schema', 'table'].map((level) => ({
							label: level.charAt(0).toUpperCase() + level.slice(1) + 's',
							value: level as ResourceLevel,
						}))}
						label="Resource level"
						name="resourceLevel"
						placeholder="Select"
						value={resourceLevel}
						onChange={handleResourceLevelChange}
						onBlur={formik.handleBlur}
					/>
				)}
				{withResourcesSelector && (
					<ResourceSelector
						accessRequestPreference={
							integrationOptions.find(
								(option) => option.value === formik.values.integrationId
							)?.accessRequestPreference
						}
						integrationId={formik.values.integrationId}
						label="Resources"
						name="resources"
						placeholder="Select"
						initialSelectedValues={formik.values.resources ?? []}
						error={formik.errors.resources}
						resourceLevel={resourceLevel || 'table'}
						onChange={(resourcesValue) => {
							formik.setFieldValue('resources', resourcesValue, true);
							formik.validateField('resources');
						}}
						onBlur={formik.handleBlur}
						disabled={
							!formik.values.integrationId || hasNoResources || !resourceLevel
						}
						onResourcesLoaded={handleOnResourcesLoaded}
						singleSelect={servicenowAccessRequestForm}
					/>
				)}
				{!servicenowAccessRequestForm && (
					<DateTimePicker
						label="Access end date"
						placeholder="Indefinite"
						name="duration"
						minDate={dayjs().toDate()}
						value={formik.values.duration}
						onChange={(durationValue) =>
							formik.setFieldValue('duration', durationValue, true)
						}
						clearable
						error={formik.errors.duration}
						onBlur={formik.handleBlur}
						popoverProps={{
							withinPortal: true,
						}}
						disabled={hasNoResources}
					/>
				)}
				{!servicenowAccessRequestForm && (
					<TextArea
						name="reason"
						label="Reason"
						optional
						minRows={3}
						value={formik.values.reason}
						error={formik.errors.reason}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						disabled={hasNoResources}
					/>
				)}
				{servicenowAccessRequestForm && (
					<>
						<TextArea
							name="requestingDepartment"
							label="Requesting Department"
							minRows={1}
							value={formik.values.requestingDepartment}
							error={formik.errors.requestingDepartment}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							disabled={hasNoResources}
						/>
						<TextArea
							name="dataNeedExplanation"
							label="Explain data needs? Any specific business logic or KPI needed?"
							minRows={3}
							value={formik.values.dataNeedExplanation}
							error={formik.errors.dataNeedExplanation}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							disabled={hasNoResources}
						/>
						<TextArea
							name="dataSharedExternallyDescription"
							label="Will this data be shared externally? Explain"
							minRows={3}
							value={formik.values.dataSharedExternallyDescription}
							error={formik.errors.dataSharedExternallyDescription}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							disabled={hasNoResources}
						/>
						<TextArea
							name="dataUsageDescription"
							label="How will this data be used in combination with other data/ source. Explain?"
							minRows={3}
							value={formik.values.dataUsageDescription}
							error={formik.errors.dataUsageDescription}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							disabled={hasNoResources}
						/>
						<TextArea
							name="additionalInformation"
							label="Any additional information?"
							minRows={3}
							value={formik.values.additionalInformation}
							error={formik.errors.additionalInformation}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							disabled={hasNoResources}
						/>
					</>
				)}
			</Stack>
			<Divider my="md" orientation="horizontal" />
			<Group spacing="md" position="apart" align="baseline">
				<Text size="sm">
					Learn more about{' '}
					<a
						href="https://docs.secoda.com/features/access-requests"
						target="_blank"
					>
						access requests
					</a>
				</Text>
				<Group spacing="xs">
					<Button onClick={handleClose}>Cancel</Button>
					<Button
						variant="primary"
						type="submit"
						disabled={
							isLoading ||
							!formik.isValid ||
							(formik.values.resources?.length || 0) === 0 ||
							hasNoResources
						}
						loading={isLoading}
					>
						{request?.id ? 'Update request' : 'Create request'}
					</Button>
				</Group>
			</Group>
		</form>
	);
}
